import React, { useState } from 'react';
import {
  Page,
  Text,
  View,
  Document,
  StyleSheet,
  Image,
  pdf,
  Font,
} from '@react-pdf/renderer';
import { Tooltip, IconButton } from '@chakra-ui/react';
import LogoColegio from '../../assets/img/logoColegio.png';
import FirmaRector from '../../assets/img/firmaRector.png'; // Importa la firma del rector
import { useDispatch, useSelector } from 'react-redux';
import { getAllNotasByStudent } from '../../features/notaSlice';
import globalInformation from '../../helpers/globalInformation';
import { CustomToast } from '../../helpers/toast';
import { MdFileOpen } from 'react-icons/md';
import Helvetica from '../../assets/fonts/Helvetica.ttf';
import HelveticaBold from '../../assets/fonts/Helvetica-Bold.ttf';

// Registrar fuente normal
Font.register({
  family: 'Helvetica',
  src: Helvetica,
});

// Registrar fuente bold como familia separada
Font.register({
  family: 'Helvetica-Bold',
  src: HelveticaBold,
});

// Estilos para certificado oficial
const styles = StyleSheet.create({
  page: {
    padding: 30,
    backgroundColor: '#FFFFFF',
    fontSize: 10,
    fontFamily: 'Helvetica',
    position: 'relative',
  },

  // Marca de agua (logo de fondo)
  watermark: {
    position: 'absolute',
    top: '35%',
    left: '25%',
    opacity: 0.08,
    width: 300,
    height: 300,
  },

  // Encabezado con logos y entidades
  headerContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 10,
    borderBottomWidth: 3,
    borderBottomColor: '#1e3a8a', // Azul profesional
    paddingBottom: 10,
    backgroundColor: '#f0f4f8', // Fondo azul muy claro
    padding: 10,
    borderRadius: 5,
  },

  logoLeft: {
    width: 60,
    height: 60,
  },

  logoRight: {
    width: 60,
    height: 60,
  },

  headerCenter: {
    flex: 1,
    alignItems: 'center',
    paddingHorizontal: 10,
  },

  governmentText: {
    fontSize: 8,
    textAlign: 'center',
    marginBottom: 2,
    color: '#1e3a8a',
    fontFamily: 'Helvetica-Bold',
  },

  schoolName: {
    fontSize: 12,
    fontFamily: 'Helvetica-Bold',
    textAlign: 'center',
    marginTop: 3,
    marginBottom: 2,
    color: '#1e3a8a',
    letterSpacing: 0.5,
  },

  schoolDecrees: {
    fontSize: 7,
    textAlign: 'center',
    marginBottom: 1,
    color: '#000000',
  },

  schoolCode: {
    fontSize: 7,
    textAlign: 'center',
    marginTop: 2,
    color: '#000000',
  },

  // Título CERTIFICA - Más profesional
  certificaTitle: {
    fontSize: 18,
    fontFamily: 'Helvetica-Bold',
    textAlign: 'center',
    marginTop: 0,
    marginBottom: 10,
    color: '#1e3a8a',
    textTransform: 'uppercase',
    letterSpacing: 3,
    textDecoration: 'underline',
    backgroundColor: '#f0f4f8',
    padding: 10,
    borderRadius: 5,
  },

  // Texto descriptivo del estudiante
  descriptionText: {
    fontSize: 10,
    textAlign: 'justify',
    lineHeight: 1.5,
    marginBottom: 10,
    color: '#000000',
  },

  rectorTitle: {
    fontSize: 8,
    fontFamily: 'Helvetica-Bold',
    textAlign: 'center',
    lineHeight: 1.5,
    marginBottom: 5,
    color: '#1e3a8a',
    textTransform: 'uppercase',
  },

  boldText: {
    fontFamily: 'Helvetica-Bold',
  },

  // Tabla simple de 3 columnas
  table: {
    width: '100%',
    borderWidth: 2,
    borderColor: '#1e3a8a',
    marginTop: 0,
    marginBottom: 0,
    borderRadius: 5,
    overflow: 'hidden',
  },

  tableHeaderRow: {
    flexDirection: 'row',
    backgroundColor: '#1e3a8a',
    borderBottomWidth: 2,
    borderBottomColor: '#1e3a8a',
  },

  tableRow: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderBottomColor: '#cbd5e1',
    minHeight: 15,
    backgroundColor: '#ffffff',
  },

  tableRowAlt: {
    backgroundColor: '#f8fafc',
  },

  tableCell: {
    padding: 5,
    borderRightWidth: 1,
    borderRightColor: '#cbd5e1',
    justifyContent: 'center',
  },

  tableCellNoBorder: {
    padding: 5,
    justifyContent: 'center',
  },

  tableHeaderText: {
    fontSize: 9,
    fontFamily: 'Helvetica-Bold',
    textAlign: 'center',
    color: '#ffffff',
    letterSpacing: 0.3,
  },

  tableCellText: {
    fontSize: 9,
    color: '#000000',
  },

  tableCellTextCenter: {
    fontSize: 9,
    textAlign: 'center',
    color: '#000000',
  },

  // Anchos de columnas
  colSubject: {
    width: '60%',
  },

  colIntensity: {
    width: '20%',
  },

  colPerformance: {
    width: '20%',
  },

  // Texto de resultado final - Más destacado
  resultText: {
    fontSize: 12,
    fontFamily: 'Helvetica-Bold',
    textAlign: 'center',
    marginTop: 10,
    marginBottom: 10,
    color: '#1e3a8a',
    letterSpacing: 1.5,
    textDecoration: 'underline',
    backgroundColor: '#f0f4f8',
    padding: 8,
    borderRadius: 5,
  },

  // Texto de fecha
  dateText: {
    fontSize: 9,
    textAlign: 'justify',
    marginBottom: 10,
    color: '#000000',
  },

  // Firma
  signatureContainer: {
    marginTop: 5,
    alignItems: 'center',
  },

  signatureImage: {
    width: 120,
    height: 50,
    marginBottom: 5,
  },

  signatureLine: {
    width: 250,
    borderTopWidth: 1,
    borderTopColor: '#000000',
    marginBottom: 5,
  },

  signatureText: {
    fontSize: 9,
    textAlign: 'center',
    color: '#000000',
  },

  signatureName: {
    fontSize: 10,
    fontFamily: 'Helvetica-Bold',
    textAlign: 'center',
    color: '#000000',
    letterSpacing: 0.5,
  },

  signatureRole: {
    fontSize: 8,
    textAlign: 'center',
    color: '#000000',
    marginTop: 2,
  },

  // Footer
  footerText: {
    fontSize: 7,
    textAlign: 'center',
    marginTop: 10,
    fontStyle: 'italic',
    color: '#000000',
    lineHeight: 1.4,
  },

  footerBold: {
    fontFamily: 'Helvetica-Bold',
    fontSize: 8,
  },
});

// Función para determinar el desempeño según la calificación
const getPerformanceLevel = average => {
  if (average >= 4.7) return 'SUPERIOR';
  if (average >= 4.0) return 'ALTO';
  if (average >= 3.0) return 'BASICO';
  return 'BAJO';
};

const API_URL = process.env.REACT_APP_API_URL;
const BASE_URL = API_URL && API_URL.split('/api')[0];

// Componente principal del reporte
const AcademicReportPDF = ({
  studentData,
  subjectData,
  sede,
  condicionFinal,
  siguienteGrado,
  schoolInfo
}) => {
  // Extraer información del estudiante
  const student = studentData?.estudiante || {};
  const academicYear = studentData?.academic_year || '2025';
  const grade = studentData?.grado || {};

  // Obtener fecha actual
  const fechaActual = new Date();
  const dia = fechaActual.getDate();
  const meses = [
    'enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio',
    'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'
  ];
  const mes = meses[fechaActual.getMonth()];
  const año = fechaActual.getFullYear();

  const logoToUse = schoolInfo?.logoUrl || LogoColegio;
  const schoolNameToUse = schoolInfo?.name || globalInformation?.colegioNombre || 'INSTITUCIÓN EDUCATIVA EL ASERRÍO';

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        {/* Marca de agua - Logo de fondo */}
        <Image src={logoToUse} style={styles.watermark} fixed />

        {/* Encabezado oficial */}
        <View style={styles.headerContainer}>
          <Image src={logoToUse} style={styles.logoLeft} />

          <View style={styles.headerCenter}>
            <Text style={styles.governmentText}>
              gobernación de norte de Santander
            </Text>
            <Text style={styles.governmentText}>
              secretaría de educación departamental
            </Text>
            <Text style={styles.governmentText}>municipio de Teorama</Text>
            <Text style={styles.schoolName}>
              {schoolNameToUse.toUpperCase()}
            </Text>
            {globalInformation?.decretos?.map((decreto, index) => (
              <Text key={index} style={styles.schoolDecrees}>
                {decreto?.tipo} de Creación N° {decreto.numero} de{' '}
                {decreto.fecha}
              </Text>
            ))}
            <Text style={styles.schoolCode}>
              CÓDIGO DE DANE: {sede?.codigoDane || '254880000682'}
            </Text>
          </View>

          <Image src={logoToUse} style={styles.logoRight} />
        </View>

        {/* Título del rector */}
        <Text style={styles.rectorTitle}>
          EL RECTOR DE LA {schoolNameToUse.toUpperCase()}{'\n'}DEL MUNICIPIO DE
          TEORAMA NORTE DE SANTANDER
        </Text>

        {/* Título CERTIFICA */}
        <Text style={styles.certificaTitle}>C E R T I F I C A</Text>

        {/* Descripción del estudiante */}
        <Text style={styles.descriptionText}>
          Que:{' '}
          <Text style={styles.boldText}>
            {student?.nombres?.toUpperCase()}{' '}
            {student?.apellidos?.toUpperCase()}
          </Text>{' '}
          Identificado(a) con Doc.{' '}
          <Text style={styles.boldText}>{student?.dni}</Text> Curso en la
          {schoolNameToUse}, año lectivo {academicYear?.year},
          grado <Text style={styles.boldText}>{grade?.nombre}</Text> de{' '}
          {grade?.nivel}, de acuerdo con los programas determinados por el
          Ministerio de Educación Nacional mediante la Ley 115 de 1994 y el
          Decreto 1860 de 1994 y obtuvo los resultados en cada una de las áreas.
        </Text>

        {/* Tabla simple de 3 columnas */}
        <View style={styles.table}>
          {/* Encabezado */}
          <View style={styles.tableHeaderRow}>
            <View style={[styles.tableCell, styles.colSubject]}>
              <Text style={styles.tableHeaderText}>ÁREAS Y/O ASIGNATURAS</Text>
            </View>
            <View style={[styles.tableCell, styles.colIntensity]}>
              <Text style={styles.tableHeaderText}>
                INTENSIDAD{'\n'}HORARIA
              </Text>
            </View>
            <View style={[styles.tableCellNoBorder, styles.colPerformance]}>
              <Text style={styles.tableHeaderText}>
                NIVEL DE{'\n'}DESEMPEÑO
              </Text>
            </View>
          </View>

          {/* Filas de materias */}
          {(subjectData || []).map((subject, index) => {
            const promedio = parseFloat(subject?.promedio) || 0;
            const ih = subject?.materia?.intensidadHorariaSemanal || 0;
            const performanceLevel = getPerformanceLevel(promedio);

            if (subject?.materia?.esComportamental) {
              return null;
            }

            return (
              <View
                key={index}
                style={[
                  styles.tableRow,
                  index % 2 === 0 ? styles.tableRowAlt : null,
                ]}
              >
                <View style={[styles.tableCell, styles.colSubject]}>
                  <Text style={styles.tableCellText}>
                    {subject?.materia?.nombre || 'MATERIA'}
                  </Text>
                </View>
                <View style={[styles.tableCell, styles.colIntensity]}>
                  <Text style={styles.tableCellTextCenter}>{ih || '—'}</Text>
                </View>
                <View style={[styles.tableCellNoBorder, styles.colPerformance]}>
                  <Text style={styles.tableCellTextCenter}>
                    {performanceLevel}
                  </Text>
                </View>
              </View>
            );
          })}
        </View>

        {/* Texto de resultado */}
        {
          condicionFinal === 'APROBADO' ? (
            <Text style={styles.resultText}>
              {`${condicionFinal} A ${siguienteGrado?.nombre}`}
            </Text>
          ) : (
            <Text style={styles.resultText}>
              {`${condicionFinal} - ${grade?.nombre?.toUpperCase()}`}
            </Text>
          )
        }

        {/* Fecha */}
        <Text style={{ fontSize: 8, textAlign: 'center' }}>
          Dado en el corregimiento El Aserrío, a los {dia} días del mes de {mes} del {año}.
        </Text>

        {/* Firma del rector */}
        <View style={styles.signatureContainer}>
          <Image src={FirmaRector} style={styles.signatureImage} />
          <View style={styles.signatureLine} />
          <Text style={styles.signatureName}>
            Lic.{' '}
            {globalInformation?.rector?.toUpperCase() ||
              'RAÚL JHONEDISON MARTÍNEZ SUÁREZ'}
          </Text>
          <Text style={styles.signatureText}>
            Rector {schoolNameToUse} - Municipio de Teorama
          </Text>
          <Text style={styles.signatureRole}>
            Acto de Posesión No. 003585 del 10 de enero del 2024
          </Text>
        </View>

        {/* Footer */}
        <Text style={styles.footerText}>
          <Text style={styles.footerBold}>
            {schoolNameToUse}
          </Text>
          {'\n'}
          <Text style={styles.footerBold}>"EDUCAR ES CREAR VOLUNTADES"</Text>
          {'\n'}
          Corregimiento El Aserrío - Teorama cel.: 3208874492 E-mail:
          ieaserrio@gmail.com
        </Text>
      </Page>
    </Document>
  );
};

// Componente botón para generar y descargar el reporte
const ReporteCertificadoEstudiante = ({
  data,
  configuracion,
}) => {
  const [isGenerating, setIsGenerating] = useState(false);
  const dispatch = useDispatch();

  const { sedeSeleccionada, user } = useSelector(state => state.auth);

  const defaultSchoolInfo = {
    name: configuracion?.nombreColegio || globalInformation.colegioNombre || 'INSTITUCION EDUCATIVA EL ASERRÍO',
    logoUrl: configuracion?.logo ? `${BASE_URL}/uploads/${configuracion.logo}` : globalInformation.logoColegio,
    address: globalInformation.direccionColegio,
    phone: globalInformation.telefonoColegio,
    email: globalInformation.emailColegio,
  };

  const generateAndDownloadPdf = async () => {
    if (!data?._id) {
      CustomToast({
        title: 'Error',
        message: 'No se encontraron datos del estudiante',
        type: 'error',
        duration: 3000,
        position: 'top',
      });
      return;
    }

    setIsGenerating(true);

    try {
      // Obtener notas del estudiante
      const notas = await dispatch(getAllNotasByStudent(data)).unwrap();

      // Validar que haya datos
      if (!notas?.notas || !notas?.notas.length) {
        throw new Error(
          'No se encontraron datos de calificaciones para este estudiante'
        );
      }

      // Generar el PDF
      const pdfDoc = (
        <AcademicReportPDF
          studentData={data}
          sede={sedeSeleccionada}
          subjectData={notas?.notas || []}
          schoolInfo={defaultSchoolInfo}
          condicionFinal={notas?.condicionFinal}
          siguienteGrado={notas?.siguienteGrado}
        />
      );

      // Crear blob y descargar
      const blob = await pdf(pdfDoc).toBlob();
      const url = URL.createObjectURL(blob);

      const fileName = `CERTIFICADO_${data?.estudiante?.nombres
        ?.toLowerCase()
        .replace(/\s+/g, '_')}_${new Date().toISOString().slice(0, 10)}.pdf`;

      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      CustomToast({
        title: 'Éxito',
        message: 'Certificado generado y descargado correctamente',
        type: 'success',
        duration: 3000,
        position: 'top',
      });
    } catch (error) {
      console.error('Error al generar PDF:', error);
      CustomToast({
        title: 'Error',
        message:
          error.msg || 'Error al generar el certificado revise si tiene notas',
        type: 'error',
        duration: 3000,
        position: 'top',
      });
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <Tooltip label="Descargar Certificado" placement="auto">
      <IconButton
        icon={<MdFileOpen />}
        size="md"
        mr={2}
        color={'white'}
        colorScheme="yellow"
        _dark={{
          bg: 'yellow.500',
          color: 'white',
          _hover: { bg: 'yellow.600' },
        }}
        isRound
        variant="solid"
        aria-label="Descargar Certificado"
        isLoading={isGenerating}
        onClick={generateAndDownloadPdf}
        isDisabled={
          user?.usuario?.rol !== 'ADMIN_ROLE' &&
          user?.usuario?.rol !== 'COORDINADOR_ROLE' &&
          configuracion?.permitirDescargarBoletin === false
        }
      />
    </Tooltip>
  );
};

export default ReporteCertificadoEstudiante;
